import { useQuery } from "@tanstack/react-query";
import cx from "classnames";
import type { ReactNode } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import {
  getListingsById,
  getListingsByIdMedia,
  getMembershipsByMembershipId,
  getMembershipsByMembershipIdRewards,
  getRetailproductsByRetailProductListingIdShopProductListingDetails,
  getShopDiscounts,
  getUser,
} from "api/methods";
import { STALE_TIME } from "api/staleTimes";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import CheckIcon from "atoms/icon/icons/ic_added_to_cart_check_ol.svg";
import { pdpFrameClassName } from "atoms/ImageCarousel/PdpImageCarousel";
import { Skeleton } from "atoms/skeleton/Skeleton";
import { usePriceDisplayState } from "hooks/usePriceDisplayState";
import { useHidePricing } from "modules/anonymousBrowsing";
import TopNav from "molecules/navbars/TopNav";
import { useFeatureFlag } from "services/Flags";
import { formatCurrency } from "toolbox/Money";
import {
  formatBillingInterval,
  getBillingIntervalBase,
  parseBillingPeriodName,
} from "utils/billingPeriod.helpers";

import { PdpDescription } from "./PdpDescription";
import { PdpGallerySkeleton } from "./PdpGallery";
import { PdpLayout } from "./PdpLayout";

// Two full rows of the listing grid's three desktop columns.
const PLACEHOLDER_CARD_COUNT = 6;
// One full row of the memberships grid at its widest, four columns.
const MEMBERSHIP_PLACEHOLDER_COUNT = 4;
// Two shelves fill a desktop screen below the banner.
const PRODUCT_SHELF_PLACEHOLDER_COUNT = 2;
// A products shelf is one row of the three-column product grid.
const PRODUCT_SHELF_CARD_COUNT = 3;
// The phone strip shows one card whole and the next one peeking.
const PRODUCT_STRIP_CARD_COUNT = 2;
// "View all" and the three collections lead the phone browse list and the desktop rail alike.
const PRODUCT_BROWSE_LINK_COUNT = 4;
// The rail's first groups: the collections, then brands and categories.
const PRODUCT_BROWSE_GROUP_COUNT = 3;
// "View all" plus the three collections the products list offers as chips.
const PRODUCT_COLLECTION_CHIP_COUNT = 4;
// One row of the design grid at its widest, four columns.
const GIFT_CARD_DESIGN_PLACEHOLDER_COUNT = 4;
// Six amounts fill whole rows of the amount grid at both two and three columns.
const GIFT_CARD_AMOUNT_PLACEHOLDER_COUNT = 6;

/** Holds the place of `ShopListHeader`'s banner. */
const ShopListHeaderSkeleton = () => (
  <Skeleton className="h-[200px] w-full rounded-none bg-gray-200 desk:h-[220px]" />
);

/** A `ProductCardLarge`: the photo beside the text on a phone, above it from `desk`. */
const ListingCardSkeleton = () => (
  <div className="flex flex-col rounded-xl bg-white p-3">
    <div className="flex gap-3 desk:flex-col desk:gap-0">
      <Skeleton className="h-32 w-24 shrink-0 rounded-xl bg-gray-200 desk:aspect-[4/5] desk:h-auto desk:w-full" />
      <div className="flex flex-1 flex-col gap-2 desk:pt-3">
        <Skeleton className="h-6 w-4/5 bg-gray-200" />
        <Skeleton className="h-4 w-full bg-gray-200" />
        <Skeleton className="h-4 w-2/3 bg-gray-200" />
      </div>
    </div>
    <Skeleton className="mt-3 h-5 w-1/3 bg-gray-200 desk:mt-2" />
  </div>
);

/** The filter rail (from `lg`) beside the results grid, laid out as both shop lists lay them. */
const ListingResultsSkeleton = () => (
  <div className="desklg:flex desklg:items-start desklg:gap-8">
    <Skeleton className="hidden h-80 rounded-xl desklg:block desklg:w-56 desklg:shrink-0 deskxl:w-64" />
    <div className="grid grid-cols-1 gap-4 desk:grid-cols-3 desklg:min-w-0 desklg:flex-1">
      {Array.from({ length: PLACEHOLDER_CARD_COUNT }, (_, index) => (
        <ListingCardSkeleton key={index} />
      ))}
    </div>
  </div>
);

/** Treatments, packages and categories (`NextFilterAndSortListings`). */
export const ShopListingSkeleton = () => (
  <div className="flex flex-col bg-two">
    <ShopListHeaderSkeleton />
    <div className="mx-auto flex w-full max-w-6xl flex-col px-6 py-4 desk:px-8 desk:py-8">
      <div className="flex h-11 items-center justify-between desklg:hidden">
        <Skeleton className="h-5 w-40 bg-gray-200" />
        <Skeleton className="h-5 w-24 bg-gray-200" />
      </div>
      <ListingResultsSkeleton />
    </div>
  </div>
);

/** A `ShopMembershipListItem`: the listing card's frame with a benefits list and a ruled footer. */
const MembershipCardSkeleton = () => (
  <div className="flex flex-col rounded-xl bg-white p-3">
    <div className="flex flex-1 items-start gap-3 desk:flex-col desk:gap-0">
      <Skeleton className="h-32 w-24 shrink-0 rounded-xl bg-gray-200 desk:aspect-[4/5] desk:h-auto desk:w-full" />
      <div className="flex min-w-0 flex-1 flex-col gap-2 desk:w-full desk:pt-3">
        <Skeleton className="h-6 w-3/5 bg-gray-200" />
        <Skeleton className="h-4 w-full bg-gray-200" />
        <Skeleton className="h-4 w-4/5 bg-gray-200" />
        <Skeleton className="h-4 w-2/3 bg-gray-200" />
      </div>
    </div>
    <div className="mt-3 border-t border-outline-grey pt-3">
      <Skeleton className="h-5 w-1/2 bg-gray-200" />
    </div>
  </div>
);

export const ShopMembershipsSkeleton = () => (
  <div className="flex flex-col bg-two">
    <ShopListHeaderSkeleton />
    <div className="mx-auto flex w-full max-w-6xl flex-col px-4 py-5 desk:px-6 desklg:px-8 desklg:py-10">
      <div className="grid grid-cols-1 items-stretch gap-5 desk:grid-cols-2 desklg:grid-cols-3 deskxl:grid-cols-4">
        {Array.from({ length: MEMBERSHIP_PLACEHOLDER_COUNT }, (_, index) => (
          <MembershipCardSkeleton key={index} />
        ))}
      </div>
    </div>
  </div>
);

/** The products landing: a best-sellers strip and browse list on a phone, shelves beside a browse rail from `desk`. */
export const ShopProductsSkeleton = () => (
  <div className="flex flex-col bg-two">
    <ShopListHeaderSkeleton />
    <div className="desk:hidden">
      <div className="flex flex-col gap-6 px-6 py-8">
        <Skeleton className="h-[35px] w-40 bg-gray-200" />
        <div className="-m-4 flex gap-6 overflow-hidden p-4">
          {Array.from({ length: PRODUCT_STRIP_CARD_COUNT }, (_, index) => (
            <div key={index} className="w-64 shrink-0">
              <Skeleton className="aspect-[4/5] w-full rounded-xl bg-gray-200" />
              <div className="pb-2 pt-3">
                <Skeleton className="h-[26px] w-4/5 bg-gray-200" />
              </div>
              <div className="pb-3 pt-1">
                <Skeleton className="h-6 w-1/2 bg-gray-200" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="border-t bg-white">
        {Array.from({ length: PRODUCT_BROWSE_LINK_COUNT }, (_, index) => (
          <div
            key={index}
            className="flex items-center gap-2 border-b border-gray-200 px-4 pb-3 pt-4"
          >
            <Skeleton className="size-8 rounded-full" />
            <Skeleton className="h-4 w-32" />
          </div>
        ))}
      </div>
    </div>
    <div className="mx-auto hidden w-full max-w-6xl gap-12 px-8 py-8 desk:flex desk:flex-col desklg:flex-row desklg:items-start desklg:gap-8 desklg:py-10">
      <div className="order-last grid grid-cols-3 gap-8 desklg:order-first desklg:flex desklg:w-56 desklg:shrink-0 desklg:flex-col desklg:gap-7 deskxl:w-64">
        {Array.from({ length: PRODUCT_BROWSE_GROUP_COUNT }, (_, group) => (
          <div key={group} className="flex flex-col">
            <div className="pb-2">
              <Skeleton className="h-[18px] w-24 bg-gray-200" />
            </div>
            {Array.from({ length: PRODUCT_BROWSE_LINK_COUNT }, (_, link) => (
              <div key={link} className="py-1.5">
                <Skeleton className="h-[18px] w-32 bg-gray-200" />
              </div>
            ))}
          </div>
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-12">
        {Array.from({ length: PRODUCT_SHELF_PLACEHOLDER_COUNT }, (_, shelf) => (
          <div key={shelf} className="flex flex-col gap-4">
            <div className="flex items-end justify-between gap-4">
              <Skeleton className="h-[35px] w-44 bg-gray-200" />
              <Skeleton className="mb-1 h-[18px] w-16 bg-gray-200" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              {Array.from({ length: PRODUCT_SHELF_CARD_COUNT }, (_, card) => (
                <ListingCardSkeleton key={card} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/** `ShopProductsListPage` while it loads: its back bar and banner over the searchable, filterable grid. */
export const PendingProductsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen flex-col bg-two">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => navigate("/shop/products")}
        contentClassName="desk:max-w-6xl desk:pl-3.5 desk:pr-8 desklg:pr-8 desk2xl:max-w-6xl"
      >
        <h1 className="text-title">{t("shopProductsListPage.browseTitle")}</h1>
      </TopNav>
      <ShopListHeaderSkeleton />
      <div className="mx-auto flex w-full max-w-6xl flex-col px-6 py-4 desk:px-8 desk:py-8">
        <div className="flex flex-col gap-3 desklg:flex-row desklg:items-center desklg:justify-between desklg:gap-6 desklg:border-b desklg:border-outline-grey desklg:pb-4">
          <div className="desk:max-w-sm desklg:order-last desklg:w-72 desklg:max-w-none desklg:shrink-0">
            <Skeleton className="h-11 w-full rounded-full bg-gray-200" />
          </div>
          <div className="flex min-w-0 items-center gap-2 overflow-hidden py-1">
            <Skeleton className="size-9 shrink-0 rounded-full bg-gray-200 desklg:hidden" />
            <Skeleton className="h-9 w-36 shrink-0 rounded-full bg-gray-200 desklg:hidden" />
            {Array.from({ length: PRODUCT_COLLECTION_CHIP_COUNT }, (_, index) => (
              <Skeleton key={index} className="h-9 w-24 shrink-0 rounded-full bg-gray-200" />
            ))}
          </div>
        </div>
        <div className="pt-4 desk:pt-6">
          <ListingResultsSkeleton />
        </div>
      </div>
    </div>
  );
};

/** A `GiftCardPurchaseForm` section: a bordered card on a phone, open on the page from `desk`. */
const GiftCardSectionSkeleton = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col gap-4 rounded-md border border-outline-grey bg-white p-4 desk:rounded-none desk:border-0 desk:bg-transparent desk:p-0">
    {children}
  </div>
);

const GiftCardHeadingSkeleton = () => <Skeleton className="h-[18px] w-40 desk:h-7" />;

const GiftCardFieldSkeleton = () => <Skeleton className="h-10 w-full rounded-md" />;

const GiftCardToggleSkeleton = () => <Skeleton className="h-9 w-full rounded-full desk:max-w-xs" />;

/** The gift card tab: the card preview beside the purchase form from `desk`, a preview strip above it on a phone. */
export const ShopGiftCardsSkeleton = () => (
  <div className="bg-gray-50 desk:bg-white">
    <div className="desk:mx-auto desk:grid desk:w-full desk:max-w-6xl desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] desk:items-start desk:gap-10 desk:px-8 desk:pb-16 desk:pt-8 desklg:gap-14">
      <div className="hidden desk:flex desk:flex-col desk:gap-4">
        <Skeleton className="aspect-[16/9] w-full rounded-2xl bg-gray-200" />
        <Skeleton className="h-20 w-full rounded-xl" />
      </div>
      <div className="min-w-0">
        <div className="flex h-[100px] gap-2 bg-gray-100 p-2 desk:hidden">
          <Skeleton className="aspect-[16/9] h-full shrink-0 rounded-md bg-gray-200" />
          <div className="flex flex-1 flex-col justify-center gap-2">
            <Skeleton className="h-7 w-14 bg-gray-200" />
            <Skeleton className="h-4 w-24 bg-gray-200" />
          </div>
        </div>
        <div className="flex flex-col gap-4 desk:gap-10">
          <div className="flex flex-col gap-4 px-2 desk:gap-10 desk:px-0">
            <GiftCardSectionSkeleton>
              <GiftCardHeadingSkeleton />
              <GiftCardToggleSkeleton />
            </GiftCardSectionSkeleton>
            <GiftCardSectionSkeleton>
              <GiftCardHeadingSkeleton />
              <div className="grid grid-cols-2 gap-4 desk:grid-cols-3 desk:gap-3 desklg:grid-cols-4">
                {Array.from({ length: GIFT_CARD_DESIGN_PLACEHOLDER_COUNT }, (_, index) => (
                  <Skeleton key={index} className="aspect-[16/9] w-full rounded-lg bg-gray-200" />
                ))}
              </div>
            </GiftCardSectionSkeleton>
            <GiftCardSectionSkeleton>
              <div className="flex flex-col gap-4 desk:gap-10">
                <div className="flex flex-col gap-4">
                  <GiftCardHeadingSkeleton />
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-4">
                      <GiftCardFieldSkeleton />
                      <GiftCardFieldSkeleton />
                    </div>
                    <div className="flex flex-col gap-2 desklg:grid desklg:grid-cols-2 desklg:gap-4">
                      <GiftCardFieldSkeleton />
                      <GiftCardFieldSkeleton />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <GiftCardHeadingSkeleton />
                  <div className="grid grid-cols-2 gap-4 desk:gap-3 desklg:grid-cols-3">
                    {Array.from({ length: GIFT_CARD_AMOUNT_PLACEHOLDER_COUNT }, (_, index) => (
                      <Skeleton key={index} className="h-20 w-full rounded-lg" />
                    ))}
                  </div>
                </div>
                <div className="flex flex-col gap-4">
                  <GiftCardHeadingSkeleton />
                  <GiftCardToggleSkeleton />
                </div>
              </div>
            </GiftCardSectionSkeleton>
            <GiftCardSectionSkeleton>
              <GiftCardHeadingSkeleton />
              <Skeleton className="h-24 w-full rounded-md" />
            </GiftCardSectionSkeleton>
          </div>
          <div className="sticky bottom-[calc(59px_+_env(safe-area-inset-bottom)-1px)] z-10 w-full bg-gray-50 p-4 desk:static desk:bg-transparent desk:p-0">
            <Skeleton className="h-11 w-full rounded-full" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

/** The detail pages' back bar; `null` holds the title's place until the type is known. */
const PendingPdpNavigation = ({ title }: { title: string | null }) => {
  const navigate = useNavigate();

  return (
    <TopNav startIconSVG={ChevronLeft} onStartIconClick={() => navigate(-1)}>
      <div className="flex max-h-[32px] items-center">
        {title === null ? (
          <Skeleton className="h-[18px] w-24" />
        ) : (
          <span className="text-body2 font-medium">{title}</span>
        )}
      </div>
    </TopNav>
  );
};

/** A photo already in the cache, framed exactly as the gallery will frame it. */
const PendingPdpPhoto = ({ src, alt }: { src: string; alt: string }) => (
  <img
    src={src}
    alt={alt}
    className={cx(pdpFrameClassName, "block bg-background-two object-cover")}
  />
);

/** The detail heading, keeping the share button's place so a title wraps where it will. */
const PendingPdpHeading = ({ children }: { children: ReactNode }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex min-w-0 flex-1 flex-col items-start gap-2">{children}</div>
    <Skeleton className="mt-1 size-[30px] shrink-0 rounded-full" />
  </div>
);

const PendingPdpTitle = ({ title }: { title: string }) => (
  <h1 className="min-w-0 break-words font-serif text-[40px] leading-[1.04] tracking-tight text-primary desklg:text-[56px]">
    {title}
  </h1>
);

const PendingPdpTitleSkeleton = () => <Skeleton className="h-[42px] w-3/4 desklg:h-[58px]" />;

const PendingPdpIntroductionSkeleton = () => (
  <div className="flex flex-col gap-2 py-1">
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-full" />
    <Skeleton className="h-4 w-2/3" />
  </div>
);

const PendingPdpPriceSkeleton = () => <Skeleton className="h-8 w-32 desk:h-9" />;

const PendingPdpButtonSkeleton = () => <Skeleton className="h-11 w-full rounded-full" />;

export const ShopPdpSkeleton = () => (
  <PdpLayout
    navigation={<PendingPdpNavigation title={null} />}
    gallery={<PdpGallerySkeleton />}
    heading={
      <PendingPdpHeading>
        <PendingPdpTitleSkeleton />
      </PendingPdpHeading>
    }
    introduction={<PendingPdpIntroductionSkeleton />}
    overview={<PendingPdpPriceSkeleton />}
    purchase={<PendingPdpButtonSkeleton />}
    details={null}
    disclosures={null}
  />
);

export const PendingPackagePdp = ({ listingId }: { listingId: string }) => {
  const { t } = useTranslation();
  const { data: listing, isPending: listingLoading } = useQuery({
    ...getListingsById.keyed({ id: listingId }),
    staleTime: STALE_TIME.catalog,
  });
  const { data: media, isPending: mediaLoading } = useQuery({
    ...getListingsByIdMedia.keyed({ id: listingId }),
    staleTime: STALE_TIME.catalog,
  });
  const { data: user, isPending: userLoading } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });
  const { data: shopDiscounts, isPending: discountsLoading } = useQuery({
    ...getShopDiscounts.keyed(),
    staleTime: 1000 * 10,
  });
  const hidePricing = useHidePricing(user);
  const discountData = shopDiscounts?.listingDiscounts?.[listingId];
  const displayState = usePriceDisplayState({
    basePriceInCents: listing?.baseCost ?? 0,
    memberPriceInCents: listing?.membershipCost,
    discountData: discountData?.isSignUpReward ? undefined : discountData,
    isGuest: hidePricing,
    hasLinearPricing: true,
    type: listing?.type === "Treatment" ? "Treatment" : "Package",
  });
  const firstMedia = media?.[0];
  const imageUrl =
    (typeof firstMedia === "string" ? firstMedia : firstMedia?.url) ?? listing?.imageUrl;

  return (
    <PendingPackagePdpBody
      navigation={
        <PendingPdpNavigation
          title={
            listing?.type
              ? listing.type === "Treatment"
                ? t("packagePDP.navTitleTreatments")
                : t("packagePDP.navTitlePackages")
              : null
          }
        />
      }
      listing={listing}
      imageUrl={imageUrl ?? undefined}
      listingLoading={listingLoading}
      mediaLoading={mediaLoading}
      pricingLoading={listingLoading || discountsLoading || userLoading}
      hidePricing={hidePricing}
      displayPrice={displayState.effectivePrice}
      originalPrice={listing?.baseCost}
      memberPrice={displayState.memberPrice}
      hasStrikethroughPrice={displayState.hasStrikethroughPrice}
      shouldShowMemberPrice={displayState.shouldShowMemberPrice}
      t={t}
    />
  );
};

export const PendingPackagePdpBody = ({
  navigation,
  listing,
  imageUrl,
  listingLoading,
  mediaLoading,
  pricingLoading,
  hidePricing,
  displayPrice,
  originalPrice,
  memberPrice,
  hasStrikethroughPrice,
  shouldShowMemberPrice,
  t,
}: {
  navigation: ReactNode;
  listing?: {
    type?: string;
    title?: string | null;
    description?: string | null;
    baseCost?: number | null;
    membershipCost?: number | null;
  };
  imageUrl?: string;
  listingLoading: boolean;
  mediaLoading: boolean;
  pricingLoading: boolean;
  hidePricing: boolean;
  displayPrice: number;
  originalPrice?: number | null;
  memberPrice: number | null;
  hasStrikethroughPrice: boolean;
  shouldShowMemberPrice: boolean;
  t: ReturnType<typeof useTranslation>["t"];
}) => (
  <PdpLayout
    navigation={navigation}
    gallery={
      imageUrl ? (
        <PendingPdpPhoto src={imageUrl} alt={listing?.title ?? ""} />
      ) : mediaLoading || listingLoading ? (
        <PdpGallerySkeleton />
      ) : null
    }
    heading={
      listing?.title ? (
        <PendingPdpHeading>
          <PendingPdpTitle title={listing.title} />
        </PendingPdpHeading>
      ) : listingLoading ? (
        <PendingPdpHeading>
          <PendingPdpTitleSkeleton />
        </PendingPdpHeading>
      ) : null
    }
    introduction={
      listing?.description ? (
        <PdpDescription description={listing.description} />
      ) : listingLoading ? (
        <PendingPdpIntroductionSkeleton />
      ) : undefined
    }
    overview={
      pricingLoading ? (
        <div className="flex flex-col items-start gap-2">
          <PendingPdpPriceSkeleton />
          <Skeleton className="h-[18px] w-44" />
        </div>
      ) : hidePricing ? (
        <div className="flex flex-col items-start text-body1">
          <span>{t("anonymousBrowsing.logInForPricing")}</span>
        </div>
      ) : originalPrice != null ? (
        <div className="flex flex-col items-start gap-2">
          <div className="text-body1">
            <span className="font-sans text-h2 desk:text-h1">{formatCurrency(displayPrice)}</span>
            {hasStrikethroughPrice && (
              <span className="pl-2 text-secondary line-through">
                {formatCurrency(originalPrice)}
              </span>
            )}
          </div>
          {shouldShowMemberPrice && memberPrice != null && (
            <div className="flex items-center gap-2 text-brand-color">
              <CheckIcon className="h-4 w-4 shrink-0 text-brand-color" />
              <span className="text-body2 font-semibold text-brand-color">
                <Trans
                  i18nKey="packagePDP.membersPayLine"
                  values={{ amount: formatCurrency(memberPrice) }}
                  components={{ bold: <span className="font-bold" /> }}
                />
              </span>
            </div>
          )}
        </div>
      ) : null
    }
    purchase={
      <div className="flex w-full flex-col gap-5">
        <Skeleton className="h-16 w-full rounded-xl" />
        <PendingPdpButtonSkeleton />
      </div>
    }
    details={null}
    disclosures={null}
  />
);

export const PendingMembershipPdp = ({ membershipId }: { membershipId: string }) => {
  const { t } = useTranslation();
  const flexibleTerms = useFeatureFlag("flexible_membership_terms");
  const { data: membership, isPending: membershipLoading } = useQuery({
    ...getMembershipsByMembershipId.keyed({ membershipId }),
    staleTime: STALE_TIME.catalog,
  });
  useQuery({
    ...getMembershipsByMembershipIdRewards.keyed({ membershipId }),
    staleTime: STALE_TIME.catalog,
  });
  const { data: user, isPending: userLoading } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const hidePricing = useHidePricing(user);
  const recurringPrice = membership
    ? flexibleTerms
      ? {
          amount: formatCurrency(membership.billingPeriodCost),
          period: `/${formatBillingInterval(
            getBillingIntervalBase(parseBillingPeriodName(membership.billingPeriodInterval)),
            membership.billingPeriodIntervalCount,
          )}`,
        }
      : { amount: formatCurrency(membership.monthlyCost), period: t("membershipPDP.perMonth") }
    : null;

  return (
    <PendingMembershipPdpBody
      navigation={<PendingPdpNavigation title={t("membershipPDP.navTitle")} />}
      membership={membership}
      membershipLoading={membershipLoading}
      userLoading={userLoading}
      hidePricing={hidePricing}
      recurringPrice={recurringPrice}
      t={t}
    />
  );
};

export const PendingMembershipPdpBody = ({
  navigation,
  membership,
  membershipLoading,
  userLoading,
  hidePricing,
  recurringPrice,
  t,
}: {
  navigation: ReactNode;
  membership?: {
    name?: string | null;
    description?: string | null;
    imageUrl?: string | null;
    setupFeeInCents?: number | null;
    commitment?: number;
  };
  membershipLoading: boolean;
  userLoading: boolean;
  hidePricing: boolean;
  /** The recurring cost split the way the page sets it: a large amount, then its period. */
  recurringPrice: { amount: string; period: string } | null;
  t: ReturnType<typeof useTranslation>["t"];
}) => (
  <PdpLayout
    navigation={navigation}
    gallery={
      membership?.imageUrl ? (
        <PendingPdpPhoto src={membership.imageUrl} alt={membership.name ?? ""} />
      ) : membershipLoading ? (
        <PdpGallerySkeleton />
      ) : null
    }
    heading={
      membership?.name ? (
        <PendingPdpHeading>
          <PendingPdpTitle title={membership.name} />
        </PendingPdpHeading>
      ) : membershipLoading ? (
        <PendingPdpHeading>
          <PendingPdpTitleSkeleton />
        </PendingPdpHeading>
      ) : null
    }
    introduction={
      membership?.description ? (
        <PdpDescription description={membership.description} />
      ) : membershipLoading ? (
        <PendingPdpIntroductionSkeleton />
      ) : undefined
    }
    overview={
      membershipLoading || userLoading ? (
        <div className="flex flex-col items-start gap-1">
          <PendingPdpPriceSkeleton />
          <Skeleton className="h-[18px] w-48" />
        </div>
      ) : (
        <div className="flex flex-col items-start gap-1">
          {hidePricing ? (
            <p className="text-body1 text-primary">{t("anonymousBrowsing.logInForPricing")}</p>
          ) : (
            recurringPrice && (
              <p className="text-body1 text-primary">
                <span className="font-sans text-h2 desk:text-h1">{recurringPrice.amount}</span>
                <span className="pl-0.5 text-secondary">{recurringPrice.period}</span>
              </p>
            )
          )}
          {!hidePricing && !!membership?.setupFeeInCents && (
            <p className="text-body2 text-secondary">
              {t("membershipPDP.enrollmentFeeNote", {
                amount: formatCurrency(membership.setupFeeInCents),
              })}
            </p>
          )}
          {membership?.commitment !== undefined && membership.commitment > 1 && (
            <Skeleton className="h-[18px] w-48" />
          )}
        </div>
      )
    }
    purchase={<PendingPdpButtonSkeleton />}
    details={null}
    disclosures={null}
  />
);

export const PendingProductPdp = ({ productId }: { productId: string }) => {
  const { t } = useTranslation();
  const { data: product, isPending: isLoading } = useQuery({
    ...getRetailproductsByRetailProductListingIdShopProductListingDetails.keyed({
      retailProductListingId: productId,
    }),
    staleTime: STALE_TIME.catalog,
  });

  return (
    <PendingProductPdpBody
      navigation={<PendingPdpNavigation title={t("productPDP.title")} />}
      name={product?.name.text}
      description={product?.description.text}
      imageUrl={product?.imageUris[0]}
      isFeatured={product?.isFeaturedProduct}
      isLoading={isLoading}
    />
  );
};

export const PendingProductPdpBody = ({
  navigation,
  name,
  description,
  imageUrl,
  isFeatured,
  isLoading,
}: {
  navigation: ReactNode;
  name?: string;
  description?: string;
  imageUrl?: string;
  isFeatured?: boolean;
  isLoading: boolean;
}) => (
  <PdpLayout
    navigation={navigation}
    gallery={
      imageUrl ? (
        <PendingPdpPhoto src={imageUrl} alt={name ?? ""} />
      ) : isLoading ? (
        <PdpGallerySkeleton />
      ) : null
    }
    heading={
      name || isLoading ? (
        <PendingPdpHeading>
          <Skeleton className="h-4 w-28" />
          {name ? <PendingPdpTitle title={name} /> : <PendingPdpTitleSkeleton />}
          {isFeatured && <Skeleton className="h-5 w-16" />}
        </PendingPdpHeading>
      ) : null
    }
    introduction={
      description ? (
        <PdpDescription description={description} />
      ) : isLoading ? (
        <PendingPdpIntroductionSkeleton />
      ) : undefined
    }
    overview={<PendingPdpPriceSkeleton />}
    purchase={
      <div className="flex items-center gap-3">
        <Skeleton className="h-11 w-28 rounded-full" />
        <Skeleton className="h-11 flex-1 rounded-full" />
      </div>
    }
    details={null}
    disclosures={null}
  />
);
